/* eslint-disable no-undef */
/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import find from 'lodash/find';
import isNil from 'lodash/isNil';
import { createPortal } from 'react-dom';
import { renderToStaticMarkup } from 'react-dom/server';

import { RouteComponentProps, useHistory, withRouter } from 'react-router-dom';
import { withTheme } from 'styled-components';
import Terminal, {
  InstallationType,
  NetworkOrigin,
  SteerableBeamPriority,
  TerminalDetails,
  NetworkGroup,
  TerminalTypeLabel,
  SpaceNetworkInfo,
} from '../../shared/entities/terminal';
import { TerminalPlatformsEnum } from '../../shared/entities/terminalPlatform';
import {
  getConnectivityColor,
  getConnectivityIcon,
  getHighestPriorityConnectivityStatus,
  isOnline,
  OFFLINE,
} from '../../utils/ConnectionStatusUtils';
import { bpsToUnitString } from '../../utils/DataRateUtils';
import { FlightPhase } from '../../utils/FlightPhaseUtils';
import {
  abbreviateNetworkName,
  EMPTY_FIELD_STRING_PLACEHOLDER,
  truncateMiddle,
  valueOrPlaceholder,
} from '../../utils/StringUtils';
import {
  getSelectedTerminalTypeLabelBasedOnTab,
  getSignalQuality,
  getTxPowerDbText,
  getSiteIdText,
  RTE_TAB,
  terminalTypeDisplayNameFromTerminalTypeLabel,
  VI_TAB,
} from '../../utils/TerminalUtilities';
import MenuItems from '../common/layout/menu/MenuItems';
import {
  CIRCLE_SIZE,
  TAIL_TAG_CIRCLE_OVERLAP_PX,
  TAIL_TAG_OFFLINE_CLASS,
  TAIL_TAG_ONLINE_CLASS,
} from '../common/map/BaseMapWrapper';
import StyledProps from '../common/theme/AppThemeInterface';
import TerminalIconContainer from './TerminalIconContainer';
import {
  TERMINAL_ICON_CLASSNAME,
  TERMINAL_ICON_FIRST_RING_CLASSNAME,
  TERMINAL_ICON_SECOND_RING_CLASSNAME,
  TERMINAL_ICON_THIRD_RING_CLASSNAME,
} from './TerminalIconStaticMarker';
import { signalQualitySvg } from './TerminalIcons';
import { areAllowedRolesPresent } from '../../utils/RBACUtils';
import { Authorities } from '../../shared/entities/user';
import { shallowEqual, useSelector } from 'react-redux';
import {
  selectAppConfig,
  selectDistanceToBeamEdgeNmMajorThreshold,
  selectDistanceToBeamEdgeNmMinorThreshold,
  selectMapRulerToolShown,
  selectUserData,
} from '../../store';
import appTheme from '../common/theme/appTheme.json';
import { SteerableBeamsRing } from '../../shared/entities/steerableBeam';
import { getSvConnectivityColor, getSvConnectivityIcon } from '../../utils/SpaceVehicleUtils';
import { LEG_WRAPPER_Z_INDEX } from '../common/map/RulerPath';
import { useSelectorWithMapId } from '../common/map/BaseMapContainer';
import { isChristmasTime } from '../../utils/SeasonalFun';

// Large number for z-index to ensure aircraft and tail tag are above anything on the map
// Updated from 9000 to 15000000 because the hubs marker clusterer sets a z-index of 1000001 to its cluster markers
const Z_INDEX_START = 15000000;
const MAX_LENGTH_BEAM_NAME = 43;
const MAX_LENGTH_TAIL_NAME = 22;
const DOUBLE_CLICK_TIMEOUT_MS = 300;

// The tab a multi-terminal tail tag opens: its own tab, or the other datapath's tab
const TAIL_TAG_TABS: Partial<Record<TerminalTypeLabel, { ownTab: number; otherTerminalTab: number }>> = {
  [TerminalTypeLabel.KA]: { ownTab: 1, otherTerminalTab: 2 },
  [TerminalTypeLabel.KU]: { ownTab: 2, otherTerminalTab: 1 },
  [TerminalTypeLabel.CWS1]: { ownTab: 0, otherTerminalTab: 1 },
  [TerminalTypeLabel.CWS2]: { ownTab: 1, otherTerminalTab: 0 },
  [TerminalTypeLabel.VI]: { ownTab: VI_TAB, otherTerminalTab: RTE_TAB },
  [TerminalTypeLabel.RTE]: { ownTab: RTE_TAB, otherTerminalTab: VI_TAB },
};

const setInnerHtml = (element: Element | null, getHtml: () => string) => {
  if (element) {
    element.innerHTML = getHtml();
  }
};

const tailTagLabelHtml = (connectivityStatus: string | undefined, color: string, label: string) =>
  renderToStaticMarkup(getConnectivityIcon(connectivityStatus ? connectivityStatus : '')) +
  `<div class="map__tailtag--terminallabel" style="color:${color};">${label}</div>`;

const spaceNetworksHtml = (spaceNetworks: SpaceNetworkInfo[]) =>
  spaceNetworks
    .map((networkInfo: SpaceNetworkInfo) => {
      const networkAvailability = networkInfo.networkAvailability ? networkInfo.networkAvailability : '';
      return (
        renderToStaticMarkup(getSvConnectivityIcon(networkAvailability)) +
        `<div class="map__tailtag--terminallabel" style="color:${getSvConnectivityColor(
          networkAvailability,
        )};">${valueOrPlaceholder(networkInfo.networkName)}&nbsp;</div>` +
        //Note: Space Vehicle metric text is very much subject to change
        `${networkInfo.networkMetricText.replace('milliwatts', 'mw')}<br/>`
      );
    })
    .join('');

const updateTailTag = (
  tailTag: HTMLDivElement | null,
  hideTailTag: boolean,
  connectivityStatus: string | undefined,
  isSelected: boolean,
) => {
  if (!tailTag) {
    return;
  }
  if (hideTailTag) {
    tailTag.style.display = 'none';
    return;
  }
  tailTag.style.display = 'flex';
  const online = isOnline(connectivityStatus);
  tailTag.classList.add(online ? TAIL_TAG_ONLINE_CLASS : TAIL_TAG_OFFLINE_CLASS);
  tailTag.classList.remove(online ? TAIL_TAG_OFFLINE_CLASS : TAIL_TAG_ONLINE_CLASS);
  tailTag.classList.toggle('map__tailtag--selected', isSelected);
};

const updateSignalStrength = (
  element: Element | null,
  flSignalQuality: number | null | undefined,
  connectivityStatus: string | undefined,
) => {
  if (!element) {
    return;
  }
  if (isNil(flSignalQuality)) {
    element.setAttribute('style', `display: none;`);
    element.innerHTML = ``;
    return;
  }
  element.setAttribute('style', `display: flex;margin-right:0px;`);
  element.innerHTML = renderToStaticMarkup(
    <img src={signalQualitySvg(getSignalQuality(connectivityStatus, flSignalQuality)!)} alt="signal strength" />,
  );
};

const updateSteerableBeamBadge = (
  element: Element | null,
  isPrimaryTerminal: boolean,
  isSecondaryTerminal: boolean,
) => {
  if (!element) {
    return;
  }
  if (isPrimaryTerminal || isSecondaryTerminal) {
    element.innerHTML = isPrimaryTerminal ? 'PRI' : 'SEC';
    element.setAttribute('style', `visibility: visible;`);
  } else {
    element.setAttribute('style', `visibility: hidden;`);
    element.innerHTML = ``;
  }
};

const getBeamEdgeDistanceColor = (distanceNm: number, minorThresholdNm: number, majorThresholdNm: number) => {
  if (distanceNm <= majorThresholdNm) {
    return appTheme.errorTextColor; // Critical
  }
  if (distanceNm <= minorThresholdNm) {
    return appTheme.warningTextColor; // Sub-optimal
  }
  return appTheme.primaryTextColor; // Nominal
};

const updateHcxBeamEdgeDistance = (
  element: Element | null,
  distanceNm: number | null,
  isSteerableBeamTerminal: boolean,
  minorThresholdNm: number,
  majorThresholdNm: number,
) => {
  if (!element) {
    return;
  }
  if (isNil(distanceNm) || !isSteerableBeamTerminal) {
    element.setAttribute('style', `visibility: hidden;`);
    element.innerHTML = ``;
    return;
  }
  const distanceColor = getBeamEdgeDistanceColor(distanceNm, minorThresholdNm, majorThresholdNm);
  element.innerHTML = renderToStaticMarkup(
    <span>
      HCX Beam Edge: <b style={{ color: distanceColor }}>{distanceNm} NM</b>
    </span>,
  );
  element.setAttribute('style', `visibility: visible;`);
};

const dataRateText = (bps: number) => (!isNil(bps) ? bpsToUnitString(bps) : EMPTY_FIELD_STRING_PLACEHOLDER);

interface MapCustomMarkerProps {
  children?: JSX.Element;
  datapathUuid: string;
  lat: number;
  lng: number;
  map: google.maps.Map;
  connectivityStatus: string;
  headingDeg: number;
  aircraftTailNumber: string;
  terminalTypeLabel: TerminalTypeLabel;
  networkGroup: NetworkGroup;
  installationType: InstallationType;
  networkOrigin: string;
  beamDisplayName: string;
  flDataRateBps: number;
  rlDataRateBps: number;
  flightPhase: string;
  flSignalQuality: number | null | undefined;
  altitudeFeet: number;
  altitudeUnit: string;
  platformType: TerminalPlatformsEnum;
  selected: boolean;
  selectTerminal: Function;
  selectedTerminalTabIndex: number;
  otherTerminalsOnAircraft: Terminal[] | null;
  otherTerminalOnAircraftSelectedId: string | undefined;
  isTailTagTransparent: boolean;
  isTailTagShown: boolean;
  sourceTime: number;
  isFullTailViewEnabled: boolean;
  isPrimaryTerminal: boolean;
  isSecondaryTerminal: boolean;
  areAnyTerminalsOnAircraftUsingSteerableBeam: boolean;
  hcxShortestDistanceToBeamEdge: number | null;
  distanceToBeamEdgeNmMinorThreshold: number;
  distanceToBeamEdgeNmMajorThreshold: number;
  disableHighestPriorityStatus?: boolean;
  disableAntennaTags?: boolean;
  useOtherTerminalConnStatus?: boolean;
  lwSiteId?: string;
  lwTxPowerDb?: number;
  spaceNetworks?: SpaceNetworkInfo[];
}

/**
 * This class is a wrapper around the google maps OverlayView api that renders the AircraftIcon as a marker
 * at a specific latitudeDeg and longitudeDeg on the map.
 */
const TerminalIconMarker = (props: MapCustomMarkerProps & StyledProps & typeof RouteComponentProps) => {
  const {
    map,
    otherTerminalsOnAircraft,
    platformType,
    datapathUuid,
    connectivityStatus,
    headingDeg,
    selected,
    aircraftTailNumber,
    terminalTypeLabel,
    networkGroup,
    installationType,
    networkOrigin,
    beamDisplayName,
    flDataRateBps,
    rlDataRateBps,
    flightPhase,
    flSignalQuality,
    altitudeFeet,
    altitudeUnit,
    children,
    isTailTagShown,
    isPrimaryTerminal,
    isSecondaryTerminal,
    useGreatestSteerableBeamPriorityIconOfAllTerminalsOnAircraft,
    isTailTagTransparent,
    otherTerminalOnAircraftSelectedId,
    disableHighestPriorityStatus,
    disableAntennaTags,
    useOtherTerminalConnStatus,
    isFullTailViewEnabled,
    selectTerminal,
    lat,
    lng,
    sourceTime,
    selectedTerminalTabIndex,
    hcxShortestDistanceToBeamEdge,
    areAnyTerminalsOnAircraftUsingSteerableBeam,
    lwSiteId,
    lwTxPowerDb,
    spaceNetworks,
  } = props;
  const history = useHistory();
  const appConfig = useSelector(selectAppConfig, shallowEqual);
  const userData = useSelector(selectUserData, shallowEqual);
  const distanceToBeamEdgeNmMinorThreshold = useSelector(selectDistanceToBeamEdgeNmMinorThreshold, shallowEqual);
  const distanceToBeamEdgeNmMajorThreshold = useSelector(selectDistanceToBeamEdgeNmMajorThreshold, shallowEqual);
  const mapRulerToolEnabled = useSelectorWithMapId(selectMapRulerToolShown, shallowEqual);
  const containerElement = useRef<HTMLDivElement>(document.createElement('div'));

  const clickDomEventListener = useRef<google.maps.MapsEventListener | null>(null);
  const mouseoverDomEventListener = useRef<google.maps.MapsEventListener | null>(null);
  const mouseoutDomEventListener = useRef<google.maps.MapsEventListener | null>(null);

  // used to differentiate click vs double click
  const clicks = useRef<number>(0);

  const [overlayView] = useState(new google.maps.OverlayView());
  const [areTailTagsShownForHover, setAreTailTagsShownForHover] = useState<boolean>(false);

  const getStatusColor = (connectivityStatus = 'offline'): string => {
    return getConnectivityColor(connectivityStatus);
  };

  // get the most recent terminals lat, lng, and heading for the aircraft icon marker
  const getMostRecentAircraftDetailsCallback = useCallback(() => {
    let currentLat = lat,
      currentLng = lng,
      currentHeading = headingDeg;
    let mostRecentTerminalSourceTime = sourceTime;

    if (otherTerminalsOnAircraft && otherTerminalsOnAircraft.length > 0) {
      otherTerminalsOnAircraft.forEach((otherTerminal: Terminal) => {
        if (otherTerminal) {
          const { latitudeDeg, longitudeDeg, headingDeg, sourceTime } = otherTerminal;
          if (otherTerminal.sourceTime && otherTerminal.sourceTime > mostRecentTerminalSourceTime) {
            mostRecentTerminalSourceTime = sourceTime;
            currentLat = latitudeDeg;
            currentLng = longitudeDeg;
            currentHeading = headingDeg;
          }
        }
      });
    }

    return { currentLat, currentLng, currentHeading };
  }, [lat, lng, headingDeg, sourceTime, otherTerminalsOnAircraft]);

  const getHighestPriorityStatusColorCallback = useCallback(
    (statusColor: string) => {
      let currentStatusColor = statusColor;

      if (otherTerminalsOnAircraft && otherTerminalsOnAircraft.length > 0) {
        const highestPriorityStatus = getHighestPriorityConnectivityStatus(
          connectivityStatus,
          ...otherTerminalsOnAircraft.map((term: Terminal) => term.connectivityStatus),
        );
        currentStatusColor = getStatusColor(highestPriorityStatus);
      }

      return currentStatusColor;
    },
    [otherTerminalsOnAircraft, connectivityStatus],
  );

  const getOtherTerminalConnStatusColorCallback = useCallback(() => {
    return otherTerminalsOnAircraft && otherTerminalsOnAircraft.length > 0
      ? getStatusColor(otherTerminalsOnAircraft[0].connectivityStatus)
      : getStatusColor(OFFLINE);
  }, [otherTerminalsOnAircraft]);

  const getOtherTerminalSteerableBeamPriorityCallback = useCallback(() => {
    return otherTerminalsOnAircraft && otherTerminalsOnAircraft.length > 0
      ? otherTerminalsOnAircraft[0].steerableBeamPriority || 0
      : 0;
  }, [otherTerminalsOnAircraft]);

  const getPrimaryAltitudeCallback = useCallback(() => {
    let primaryAltitude = altitudeFeet;
    if (otherTerminalsOnAircraft && otherTerminalsOnAircraft.length > 0) {
      const otherConnectivityStatus = otherTerminalsOnAircraft[0].connectivityStatus;
      if (!isOnline(connectivityStatus) && isOnline(otherConnectivityStatus)) {
        primaryAltitude = otherTerminalsOnAircraft[0].altitudeFeet;
      }
    }
    return primaryAltitude;
  }, [otherTerminalsOnAircraft, altitudeFeet, connectivityStatus]);

  const getPrimaryFlightPhaseCallback = useCallback(() => {
    let primaryFlightPhase = flightPhase;
    if (otherTerminalsOnAircraft && otherTerminalsOnAircraft.length > 0) {
      const otherConnectivityStatus = otherTerminalsOnAircraft[0].connectivityStatus;
      if (!isOnline(connectivityStatus) && isOnline(otherConnectivityStatus)) {
        primaryFlightPhase = otherTerminalsOnAircraft[0].flightPhase;
      }
    }
    return primaryFlightPhase;
  }, [flightPhase, otherTerminalsOnAircraft, connectivityStatus]);
  const drawTailTagCallback = useCallback(
    (
      terminalNum: number,
      statusColor: string,
      connectivityStatus: string | undefined,
      aircraftTailNumber: string,
      terminalTypeLabel: TerminalTypeLabel,
      installationType: InstallationType,
      networkOrigin: NetworkOrigin,
      beamName: string,
      flDataRateBps: number,
      rlDataRateBps: number,
      flightPhase: string,
      flSignalQuality: number | null | undefined,
      altitudeFeet: number,
      altitudeUnit: string,
      datapathUuid: string,
      platformType: TerminalPlatformsEnum,
      isPrimaryTerminal: boolean,
      isSecondaryTerminal: boolean,
      hcxShortestDistanceToBeamEdge: number | null,
      hideTailTag: boolean,
      lwSiteId: string,
      lwTxPowerDb: number,
      spaceNetworks: SpaceNetworkInfo[],
    ): void => {
      const container = containerElement.current;
      if (!container) {
        return;
      }
      const query = (name: string) => container.querySelector(`.map__tailtag${name}${terminalNum}`);
      const childTailTag = query('') as HTMLDivElement | null;
      const childName = query('--name');
      const childSignalStrength = query('--signal_strength');
      const childSvNetworkName = query('--sv_networks');
      const childLwNetwork = query('--lw_network');
      const childLwTxPower = query('--lw_tx_power');
      const childLwSiteId = query('--lw_site_id');
      const childBeam = query('--beam');
      const childSteerableBeamPrimaryTerminalLabel = query('--steerablebeamprimaryterminallabel');
      const childSteerableBeamPrimaryTerminalLabelBadge = query('--steerablebeamprimaryterminallabel__badge');
      const childHcxShortestDistanceToBeamEdge = query('--hcx_shortest_distance_to_beam_edge');
      const childFlRlRate = query('--flrate');

      const selectedTerminalTypeLabel = getSelectedTerminalTypeLabelBasedOnTab(selectedTerminalTabIndex, {
        installationType,
        terminalTypeLabel,
      } as TerminalDetails);

      // Change the background if this tail tag is selected. One HaloNet tail tag covers both datapaths.
      const isSelected =
        selected &&
        (installationType === InstallationType.HALONET_TERMINAL ||
          isTailTagSelected(terminalNum, terminalTypeLabel, selectedTerminalTypeLabel));
      updateTailTag(childTailTag, hideTailTag, connectivityStatus, isSelected);

      setInnerHtml(childName, () =>
        tailTagLabelHtml(
          connectivityStatus,
          statusColor,
          valueOrPlaceholder(
            truncateMiddle(terminalTypeDisplayNameFromTerminalTypeLabel(terminalTypeLabel), MAX_LENGTH_TAIL_NAME),
          ),
        ),
      );
      setInnerHtml(
        childBeam,
        () =>
          `${valueOrPlaceholder(truncateMiddle(beamName, MAX_LENGTH_BEAM_NAME))} / ${valueOrPlaceholder(
            abbreviateNetworkName(networkOrigin),
          )}`,
      );
      if (spaceNetworks) {
        setInnerHtml(childSvNetworkName, () => spaceNetworksHtml(spaceNetworks));
      }
      setInnerHtml(childLwNetwork, () =>
        tailTagLabelHtml(connectivityStatus, statusColor, valueOrPlaceholder(networkOrigin)),
      );
      setInnerHtml(childLwTxPower, () => getTxPowerDbText(lwTxPowerDb));
      setInnerHtml(childLwSiteId, () => getSiteIdText(lwSiteId));

      if (areAllowedRolesPresent(userData, Authorities.ROLE_SIGNAL_QUALITY_VIEW_ONLY)) {
        updateSignalStrength(childSignalStrength, flSignalQuality, connectivityStatus);
      }
      // Check if there are any steerable beam aircraft;
      childSteerableBeamPrimaryTerminalLabel?.setAttribute(
        'style',
        areAnyTerminalsOnAircraftUsingSteerableBeam ? `display: flex;margin-left:5px` : `display: none;`,
      );
      updateSteerableBeamBadge(childSteerableBeamPrimaryTerminalLabelBadge, isPrimaryTerminal, isSecondaryTerminal);
      updateHcxBeamEdgeDistance(
        childHcxShortestDistanceToBeamEdge,
        hcxShortestDistanceToBeamEdge,
        isPrimaryTerminal || isSecondaryTerminal,
        distanceToBeamEdgeNmMinorThreshold,
        distanceToBeamEdgeNmMajorThreshold,
      );
      if (networkOrigin !== NetworkGroup.GX) {
        setInnerHtml(childFlRlRate, () => `${dataRateText(flDataRateBps)} / ${dataRateText(rlDataRateBps)}`);
      }
    },
    [
      areAnyTerminalsOnAircraftUsingSteerableBeam,
      selected,
      selectedTerminalTabIndex,
      userData,
      distanceToBeamEdgeNmMinorThreshold,
      distanceToBeamEdgeNmMajorThreshold,
    ],
  );

  const drawOtherTailTagsCallback = useCallback(
    (altitudeUnit: string, hideTailTag: boolean) => {
      if (otherTerminalsOnAircraft && otherTerminalsOnAircraft.length > 0) {
        for (let idx = 0; idx < otherTerminalsOnAircraft.length; idx++) {
          let otherTerminal = otherTerminalsOnAircraft[idx];
          // render the tail tag if there is data for that terminal
          if (otherTerminal) {
            const {
              aircraftTailNumber,
              terminalTypeLabel,
              installationType,
              networkOrigin,
              beamName,
              beamDisplayName,
              flDataRateBps,
              rlDataRateBps,
              flSignalQuality,
              flightPhase,
              altitudeFeet,
              connectivityStatus,
              datapathUuid,
              platformType,
              steerableBeamPriority,
              steerableBeamsSummary,
              lwSiteId,
              lwTxPowerDb,
            } = otherTerminal;

            const steerableBeamData = steerableBeamsSummary?.beams.find(
              (beam: SteerableBeamsRing) => beam.beamName === beamName,
            );
            const hcxShortestDistanceToBeamEdge =
              !isNil(steerableBeamData) && !isNil(steerableBeamData.terminalShortestDistanceToBeamEdgeMapping)
                ? steerableBeamData.terminalShortestDistanceToBeamEdgeMapping[datapathUuid]
                : null;

            drawTailTagCallback(
              idx + 1,
              getStatusColor(connectivityStatus),
              connectivityStatus,
              aircraftTailNumber,
              terminalTypeLabel,
              installationType,
              networkOrigin,
              beamDisplayName,
              flDataRateBps,
              rlDataRateBps,
              flightPhase,
              flSignalQuality,
              altitudeFeet,
              altitudeUnit,
              datapathUuid,
              platformType,
              steerableBeamPriority === SteerableBeamPriority.PRIMARY,
              steerableBeamPriority === SteerableBeamPriority.SECONDARY,
              hcxShortestDistanceToBeamEdge,
              hideTailTag,
              lwSiteId,
              lwTxPowerDb,
              [],
            );
          }
        }
      }
    },
    [otherTerminalsOnAircraft, drawTailTagCallback],
  );

  const drawTailTagHeaderCallback = useCallback(
    (
      aircraftTailNumber: string,
      flightPhase: string,
      altitudeFeet: number,
      altitudeUnit: string,
      platformType: TerminalPlatformsEnum,
      hideAltitude?: boolean,
    ): void => {
      if (containerElement.current) {
        // update the tail number
        const childTailName = containerElement.current.querySelector(`.map__tailtag--tailname`) as HTMLDivElement;
        if (childTailName) {
          childTailName.innerHTML = valueOrPlaceholder(truncateMiddle(aircraftTailNumber, MAX_LENGTH_TAIL_NAME));
        }

        // update the altitude
        const childAltitude = containerElement.current.querySelector(`.map__tailtag--altitude`) as HTMLDivElement;
        const childTakeoffIcon = containerElement.current.querySelector(`.map__tailtag--takeofficon`) as HTMLDivElement;
        const childLandingIcon = containerElement.current.querySelector(`.map__tailtag--landingicon`) as HTMLDivElement;
        const inFlightIcon = containerElement.current.querySelector(`.map__tailtag--inflighticon`) as HTMLDivElement;
        const onDeckIcon = containerElement.current.querySelector(`.map__tailtag--ondeckicon`) as HTMLDivElement;

        if (childAltitude && platformType === TerminalPlatformsEnum.Airborne) {
          childAltitude.innerHTML = `${valueOrPlaceholder(altitudeFeet, (alt: number) =>
            alt.toLocaleString(),
          )} ${altitudeUnit}`;

          if (hideAltitude) {
            childAltitude.style.display = 'none';
          } else {
            childAltitude.style.display = 'inline-block';
          }

          if (childLandingIcon && childTakeoffIcon && inFlightIcon && onDeckIcon) {
            if (flightPhase) {
              switch (flightPhase) {
                case FlightPhase.ascending:
                  childLandingIcon.style.display = 'none';
                  childTakeoffIcon.style.display = 'block';
                  inFlightIcon.style.display = 'none';
                  onDeckIcon.style.display = 'none';
                  break;
                case FlightPhase.descending:
                  childLandingIcon.style.display = 'block';
                  childTakeoffIcon.style.display = 'none';
                  inFlightIcon.style.display = 'none';
                  onDeckIcon.style.display = 'none';
                  break;
                case FlightPhase.inFlight:
                  childLandingIcon.style.display = 'none';
                  childTakeoffIcon.style.display = 'none';
                  inFlightIcon.style.display = 'block';
                  onDeckIcon.style.display = 'none';
                  break;
                case FlightPhase.onDeck:
                  childLandingIcon.style.display = 'none';
                  childTakeoffIcon.style.display = 'none';
                  inFlightIcon.style.display = 'none';
                  onDeckIcon.style.display = 'block';
                  break;
                default:
                  childLandingIcon.style.display = 'none';
                  childTakeoffIcon.style.display = 'none';
                  inFlightIcon.style.display = 'none';
                  onDeckIcon.style.display = 'none';
                  break;
              }
            } else {
              childLandingIcon.style.display = 'none';
              childTakeoffIcon.style.display = 'none';
              inFlightIcon.style.display = 'none';
              onDeckIcon.style.display = 'none';
            }
          }
        }
      }
    },
    [],
  );
  // Memoize the draw method to change when props change
  const drawCallback = useCallback((): void => {
    let statusColor = getStatusColor(connectivityStatus);
    let showPrimarySteerableRings = isPrimaryTerminal;
    let showSecondarySteerableRing = isSecondaryTerminal;
    const overlayProjection = overlayView.getProjection();
    if (containerElement.current && overlayProjection) {
      let { currentLat, currentLng, currentHeading } = getMostRecentAircraftDetailsCallback();
      let currentStatusColor = statusColor;

      if (!disableHighestPriorityStatus) {
        currentStatusColor = getHighestPriorityStatusColorCallback(statusColor);
      } else if (useOtherTerminalConnStatus) {
        currentStatusColor = getOtherTerminalConnStatusColorCallback();
        showPrimarySteerableRings = getOtherTerminalSteerableBeamPriorityCallback() === SteerableBeamPriority.PRIMARY;
        showSecondarySteerableRing =
          getOtherTerminalSteerableBeamPriorityCallback() === SteerableBeamPriority.SECONDARY;
      }

      const point = overlayProjection.fromLatLngToDivPixel(new google.maps.LatLng(currentLat, currentLng));

      // Add the truncated horizontal x value of the map to a starting index for the z-index.
      // This will make the aircraft farthest right on the screen to show above any aircraft to the left.
      // If the UP Ruler tool is enabled, ensure the tail tags appear below the ruler polylines (with some buffer)
      // by ensuring the z-index is between minRulerZIndex and maxRulerZIndex with respect to point.x.
      const minRulerZIndex = 2;
      const maxRulerZIndex = LEG_WRAPPER_Z_INDEX - 2;
      const mapWidth = map.getDiv().offsetWidth;
      const normalizedX = (point.x + mapWidth / 2) / mapWidth; // Normalize point.x to a range of [0, 1]
      const zIndex = !mapRulerToolEnabled
        ? Z_INDEX_START + (point.x | 0)
        : Math.round(
            Math.min(
              Math.max(minRulerZIndex, minRulerZIndex + normalizedX * (maxRulerZIndex - minRulerZIndex)),
              maxRulerZIndex,
            ),
          );

      containerElement.current.style.left = `${point.x}px`;
      containerElement.current.style.top = `${point.y}px`;

      if (selected) {
        if (mapRulerToolEnabled) {
          containerElement.current.classList.add('map__aircraftcontainer--below_ruler_components');
        } else {
          containerElement.current.classList.remove('map__aircraftcontainer--below_ruler_components');
        }

        containerElement.current.classList.add('map__aircraftcontainer--selected');
      } else {
        containerElement.current.classList.remove(
          'map__aircraftcontainer--selected',
          'map__aircraftcontainer--below_ruler_components',
        );
        containerElement.current.style.zIndex = zIndex.toString();
      }

      // Set the terminal icon and tail tags to be transparent
      if (isTailTagTransparent) {
        containerElement.current.classList.add('map__aircraftcontainer--transparent');
      } else {
        containerElement.current.classList.remove('map__aircraftcontainer--transparent');
      }

      const childIcon = containerElement.current.querySelector(`.${TERMINAL_ICON_CLASSNAME}`) as SVGSVGElement;
      const firstRing = containerElement.current.querySelector(
        `.${TERMINAL_ICON_FIRST_RING_CLASSNAME}`,
      ) as SVGCircleElement;
      const secondRing = containerElement.current.querySelector(
        `.${TERMINAL_ICON_SECOND_RING_CLASSNAME}`,
      ) as SVGCircleElement;
      const thirdRing = containerElement.current.querySelector(
        `.${TERMINAL_ICON_THIRD_RING_CLASSNAME}`,
      ) as SVGCircleElement;
      if (childIcon) {
        if (headingDeg) {
          if (isChristmasTime(appConfig)) {
            childIcon.style.transform = `rotate(${currentHeading + 90}deg)`;
            if (currentHeading < 180) {
              childIcon.style.transform += ' scaleY(-1)'; // Mirror the icon when heading right
            }
          } else {
            childIcon.style.transform = `rotate(${currentHeading}deg)`;
          }
        }
        firstRing.style.fill = `${currentStatusColor}`;

        // Add the Secondary Terminal Ring to the Aircraft Icon by checking if any of the terminals on the aircraft are primary or secondary
        if (
          // Is the main terminal (According to the logic that places terminals on the map) one of the primary terminals
          showPrimarySteerableRings ||
          // Is the main terminal (According to the logic that places terminals on the map) one of the secondary terminals
          showSecondarySteerableRing ||
          // When not on FTV, also check if any of the terminals on the aircraft one of the primary terminals
          (useGreatestSteerableBeamPriorityIconOfAllTerminalsOnAircraft &&
            find(
              otherTerminalsOnAircraft,
              (terminalOnAircraft: Terminal) =>
                terminalOnAircraft.steerableBeamPriority === SteerableBeamPriority.PRIMARY,
            )) ||
          // When not on FTV, also check if any of the terminals on the aircraft one of the secondary terminals
          (useGreatestSteerableBeamPriorityIconOfAllTerminalsOnAircraft &&
            find(
              otherTerminalsOnAircraft,
              (terminalOnAircraft: Terminal) =>
                terminalOnAircraft.steerableBeamPriority === SteerableBeamPriority.SECONDARY,
            ))
        ) {
          secondRing.style.display = 'block';

          // Add the Primary Terminal Ring to the Aircraft Icon by checking if any of the terminals on the aircraft are primary
          if (
            // Is the main terminal (According to the logic that places terminals on the map) one of the primary terminals
            showPrimarySteerableRings ||
            // When not on FTV, also check if any of the terminals on the aircraft one of the primary terminals
            (useGreatestSteerableBeamPriorityIconOfAllTerminalsOnAircraft &&
              find(
                otherTerminalsOnAircraft,
                (terminalOnAircraft: Terminal) =>
                  terminalOnAircraft.steerableBeamPriority === SteerableBeamPriority.PRIMARY,
              ))
          ) {
            thirdRing.style.display = 'block';
          } else {
            thirdRing.style.display = 'none';
          }
        } else {
          secondRing.style.display = 'none';
          thirdRing.style.display = 'none';
        }
      }

      const tailTagContainer = containerElement.current.querySelector('.map__tailtag--container') as HTMLDivElement;
      const tailTagHeaderDivider = containerElement.current.querySelectorAll(
        '.map__tailtag--divider',
      ) as NodeListOf<HTMLDivElement>;

      const headingNum = currentHeading ? currentHeading : 0;
      const areTailTagsShown = isTailTagShown || areTailTagsShownForHover || selected;

      const primaryAltitude = getPrimaryAltitudeCallback();
      const primaryFlightPhase = getPrimaryFlightPhaseCallback();

      drawTailTagHeaderCallback(aircraftTailNumber, primaryFlightPhase, primaryAltitude, altitudeUnit, platformType);

      drawTailTagCallback(
        0,
        statusColor,
        connectivityStatus,
        aircraftTailNumber,
        terminalTypeLabel,
        installationType,
        networkOrigin,
        beamDisplayName,
        flDataRateBps,
        rlDataRateBps,
        flightPhase,
        flSignalQuality,
        altitudeFeet,
        altitudeUnit,
        datapathUuid,
        platformType,
        isPrimaryTerminal,
        isSecondaryTerminal,
        hcxShortestDistanceToBeamEdge,
        disableAntennaTags,
        lwSiteId,
        lwTxPowerDb,
        spaceNetworks,
      );

      drawOtherTailTagsCallback(altitudeUnit, disableAntennaTags);

      if (!disableAntennaTags) {
        if (tailTagHeaderDivider) {
          tailTagHeaderDivider.forEach(function (divider: HTMLDivElement) {
            divider.style.display = 'block';
          });
        }
      } else {
        // Remove the divider
        if (tailTagHeaderDivider) {
          tailTagHeaderDivider.forEach(function (divider: HTMLDivElement) {
            divider.style.display = 'none';
          });
        }
      }

      if (tailTagContainer) {
        if (!areTailTagsShown) {
          tailTagContainer.style.display = 'none';
        } else {
          tailTagContainer.style.display = 'inline-block';

          // mark the container as having one tail tag as selected
          if (otherTerminalOnAircraftSelectedId || selected) {
            tailTagContainer.classList.add('map__tailtagcontainer--selected');
          } else {
            tailTagContainer.classList.remove('map__tailtagcontainer--selected');
          }

          // Move the tail tag container above the aircraft if heading north. Otherwise tail tag is below aircraft.
          if (
            (headingNum > -90 && headingNum < 90) ||
            (headingNum > 270 && headingNum < 360) ||
            (headingNum < -270 && headingNum > -360)
          ) {
            const rect = tailTagContainer.getBoundingClientRect();
            let topAmount = Math.floor(rect.height) - TAIL_TAG_CIRCLE_OVERLAP_PX - 2; // - 2 for the border
            tailTagContainer.style.top = `-${topAmount}px`;
            tailTagContainer.style.left = `${CIRCLE_SIZE - TAIL_TAG_CIRCLE_OVERLAP_PX}px`;
          } else {
            tailTagContainer.style.top = `${CIRCLE_SIZE - TAIL_TAG_CIRCLE_OVERLAP_PX}px`;
            tailTagContainer.style.left = `${CIRCLE_SIZE - TAIL_TAG_CIRCLE_OVERLAP_PX}px`;
          }
        }
      }
    }
  }, [
    areTailTagsShownForHover,
    otherTerminalsOnAircraft,
    platformType,
    datapathUuid,
    connectivityStatus,
    headingDeg,
    selected,
    aircraftTailNumber,
    terminalTypeLabel,
    installationType,
    networkOrigin,
    beamDisplayName,
    flDataRateBps,
    rlDataRateBps,
    flightPhase,
    flSignalQuality,
    altitudeFeet,
    altitudeUnit,
    isTailTagShown,
    isPrimaryTerminal,
    isSecondaryTerminal,
    hcxShortestDistanceToBeamEdge,
    useGreatestSteerableBeamPriorityIconOfAllTerminalsOnAircraft,
    isTailTagTransparent,
    otherTerminalOnAircraftSelectedId,
    disableHighestPriorityStatus,
    disableAntennaTags,
    useOtherTerminalConnStatus,
    drawOtherTailTagsCallback,
    drawTailTagCallback,
    drawTailTagHeaderCallback,
    getHighestPriorityStatusColorCallback,
    getMostRecentAircraftDetailsCallback,
    getPrimaryAltitudeCallback,
    getPrimaryFlightPhaseCallback,
    getOtherTerminalConnStatusColorCallback,
    getOtherTerminalSteerableBeamPriorityCallback,
    overlayView,
    lwSiteId,
    lwTxPowerDb,
    spaceNetworks,
    mapRulerToolEnabled,
    map,
    appConfig,
  ]);
  const onTerminalClickCallback = useCallback(
    (isDoubleClick: boolean, event: Event): void => {
      let otherTerminalClicked = false;
      let isMultiTerminalAircraft = false;
      let headerClicked = !isNil((event.target as HTMLElement).closest('.map__tailtag--header'));

      // select the terminal that was clicked if multiple terminals on aircraft
      if (otherTerminalsOnAircraft && otherTerminalsOnAircraft.length > 0) {
        isMultiTerminalAircraft = true;
        for (let idx = 0; idx < otherTerminalsOnAircraft.length; idx++) {
          if ((event.target as HTMLElement).closest(`.map__tailtag${idx + 1}`)) {
            const { datapathUuid } = otherTerminalsOnAircraft[idx];
            otherTerminalClicked = true;
            const tabIndex = determineSelectedTerminalTab(
              headerClicked,
              otherTerminalClicked,
              isMultiTerminalAircraft,
              terminalTypeLabel,
            );
            selectTerminal(datapathUuid, tabIndex, isDoubleClick);

            if (isDoubleClick && isFullTailViewEnabled) {
              history.push(`/${MenuItems.fleet}/${datapathUuid}`);
            }
          }
        }
      }

      if (!otherTerminalClicked) {
        const tabIndex = determineSelectedTerminalTab(
          headerClicked,
          otherTerminalClicked,
          isMultiTerminalAircraft,
          terminalTypeLabel,
        );
        selectTerminal(datapathUuid, tabIndex, isDoubleClick);

        if (isDoubleClick && isFullTailViewEnabled) {
          history.push(`/${MenuItems.fleet}/${datapathUuid}`);
        }
      }
    },
    [history, isFullTailViewEnabled, otherTerminalsOnAircraft, selectTerminal, datapathUuid, terminalTypeLabel],
  );

  const onAddCallback = useCallback((): void => {
    if (containerElement.current) {
      containerElement.current.style.position = 'absolute';
      containerElement.current.classList.add('map__aircraftcontainer');
      containerElement.current.setAttribute('data-test', `map__aircraftcontainer__${datapathUuid}`);

      containerElement.current.innerHTML = renderToStaticMarkup(
        <TerminalIconContainer
          platformType={platformType}
          connectivityStatus={connectivityStatus}
          datapathUuid={datapathUuid}
          userData={userData}
          otherTerminalsOnAircraft={
            installationType === InstallationType.HALONET_TERMINAL ? null : otherTerminalsOnAircraft
          }
          networkGroup={networkGroup}
          appConfig={appConfig}
        />,
      );

      const panes = overlayView.getPanes();
      if (panes) {
        if (mapRulerToolEnabled) {
          // Add the overlay to the overlayLayer pane to ensure it appears in the same map pane as the ruler tool polylines
          panes.overlayLayer.appendChild(containerElement.current);
        } else {
          // Add the overlay to the overlayMouseTarget so it receives mouse events
          panes.overlayMouseTarget.appendChild(containerElement.current);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    connectivityStatus,
    otherTerminalsOnAircraft,
    installationType,
    overlayView,
    platformType,
    datapathUuid,
    flSignalQuality,
    networkGroup,
    mapRulerToolEnabled,
  ]);

  const onRemove = (): void => {
    if (containerElement.current && containerElement.current.parentNode) {
      containerElement.current.parentNode.removeChild(containerElement.current);
    }

    if (clickDomEventListener.current) {
      clickDomEventListener.current.remove();
    }

    if (mouseoutDomEventListener.current) {
      mouseoutDomEventListener.current.remove();
    }

    if (mouseoverDomEventListener.current) {
      mouseoverDomEventListener.current.remove();
    }
  };

  useEffect(() => {
    overlayView.draw = drawCallback;
    overlayView.onAdd = onAddCallback;
    overlayView.onRemove = onRemove;
    overlayView.setMap(map);

    return () => {
      // remove the overlay view from the map
      overlayView.setMap(null);

      // re add the overlay view on the map to ensure the new tail tag is drawn
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Update overlayView's onAdd method since onAddCallback has updated (mapRulerToolEnabled state has changed).
    overlayView.onAdd = onAddCallback;

    // Remove overlay view from the map (triggers onRemove callback)
    overlayView.setMap(null);

    // Re-add overlay view to the map
    // Triggers onAdd callback, thus updating the map pane it belongs to with respect to the ruler tool
    overlayView.setMap(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRulerToolEnabled, map, overlayView]);

  useEffect(() => {
    // Add listeners if the UP Ruler tool isn't enabled
    if (!mapRulerToolEnabled) {
      clickDomEventListener.current = google.maps.event.addDomListener(
        containerElement.current,
        'click',
        (event: Event) => {
          clicks.current++;

          if (clicks.current) {
            setTimeout(() => {
              if (clicks.current > 1) {
                onTerminalClickCallback(true, event);
              } else {
                onTerminalClickCallback(false, event);
              }
              clicks.current = 0;
            }, DOUBLE_CLICK_TIMEOUT_MS);
          }
        },
      );
    }

    if (!mapRulerToolEnabled) {
      mouseoverDomEventListener.current = google.maps.event.addDomListener(
        containerElement.current,
        'mouseover',
        () => {
          if (!isTailTagShown) {
            setAreTailTagsShownForHover(true);
          }
        },
      );
    }
    if (!mapRulerToolEnabled) {
      mouseoutDomEventListener.current = google.maps.event.addDomListener(containerElement.current, 'mouseout', () => {
        if (!isTailTagShown) {
          setAreTailTagsShownForHover(false);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTailTagShown, mapRulerToolEnabled]);

  useEffect(() => {
    overlayView.draw = drawCallback;
    overlayView.draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawCallback]);

  /**
   * Determine which terminal tab to use.
   * Expected single terminal tabs
   * * KA/KU/KUKA * *
   * Expected dual aircraft tabs
   * * CWS1 | CWS2 * *
   * Expected Ku + Ka tabs
   * * OVERVIEW | KA | KU * *
   * Expected HaloNet tabs
   * * VI | RTE * *
   */
  const determineSelectedTerminalTab = (
    headerClicked: boolean,
    otherTerminalClicked: boolean,
    isMultiTerminalAircraft: boolean,
    terminalTypeLabel: TerminalTypeLabel,
  ) => {
    const tabs = TAIL_TAG_TABS[terminalTypeLabel];
    if (headerClicked || !isMultiTerminalAircraft || !tabs) {
      return 0;
    }
    return otherTerminalClicked ? tabs.otherTerminalTab : tabs.ownTab;
  };

  /**
   * This helper function determines whether a tail tag
   * is selected based on which tab is currently selected.
   *
   * @param tailTagIdx the tail tag's index, where 0 is the top-most tail tag
   * @param tailTagTerminalType the terminal type of the tail tag
   * @param selectedTerminalTypeLabel the currently selected tab's terminal type
   */
  const isTailTagSelected = (
    tailTagIdx: number,
    tailTagTerminalType: TerminalTypeLabel,
    selectedTerminalTypeLabel: TerminalTypeLabel | undefined,
  ) => {
    if (tailTagIdx === 0) {
      return selectedTerminalTypeLabel === tailTagTerminalType || isNil(selectedTerminalTypeLabel);
    } else {
      return selectedTerminalTypeLabel === tailTagTerminalType;
    }
  };

  // render is used to keep this component in the virtual dom. The actual
  // DOM will be set in onAdd() for the google.maps.OverlayView()
  return createPortal(children, containerElement.current);
};

export default withRouter(withTheme(TerminalIconMarker));
